import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Briefcase, MapPin, Building2, Calendar, Clock, CheckCircle, 
  XCircle, Loader2, FileText, Send, AlertCircle, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const STATUT_CONFIG = {
  'à_l_etude': { 
    label: 'À l\'étude', 
    color: 'bg-blue-100 text-blue-700 border-blue-200', 
    icon: Clock,
    description: 'Votre candidature est en cours d\'examen'
  },
  'entretien_prévu': { 
    label: 'Entretien prévu', 
    color: 'bg-amber-100 text-amber-700 border-amber-200', 
    icon: Calendar,
    description: 'Un entretien a été programmé'
  },
  'embauché': { 
    label: 'Embauché(e)', 
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200', 
    icon: CheckCircle,
    description: 'Félicitations ! Vous avez été retenu(e)'
  },
  'non_retenu': { 
    label: 'Non retenu(e)', 
    color: 'bg-red-100 text-red-700 border-red-200', 
    icon: XCircle,
    description: 'Votre candidature n\'a pas été retenue'
  }
};

const MesCandidatures = () => {
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    en_cours: 0,
    entretiens: 0,
    embauches: 0,
    refus: 0
  });

  useEffect(() => {
    loadCandidatures();
  }, []);

  const loadCandidatures = async () => {
    setLoading(true);
    try {
      const response = await api.get('/candidatures/mes-candidatures');
      const data = response.data;
      setCandidatures(data);
      
      // Calculer les stats
      const statsCalc = {
        total: data.length,
        en_cours: data.filter(c => c.statut === 'à_l_etude').length,
        entretiens: data.filter(c => c.statut === 'entretien_prévu').length,
        embauches: data.filter(c => c.statut === 'embauché').length,
        refus: data.filter(c => c.statut === 'non_retenu').length
      };
      setStats(statsCalc);
    } catch (error) {
      toast.error('Erreur de chargement des candidatures');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-slate-50 border-slate-200 rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            <p className="text-sm text-slate-600">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200 rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{stats.en_cours}</p>
            <p className="text-sm text-blue-600">À l'étude</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200 rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{stats.entretiens}</p>
            <p className="text-sm text-amber-600">Entretiens</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200 rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-700">{stats.embauches}</p>
            <p className="text-sm text-emerald-600">Embauché(e)</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200 rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-700">{stats.refus}</p>
            <p className="text-sm text-red-600">Non retenu</p>
          </CardContent>
        </Card>
      </div>

      {/* Liste des candidatures */}
      {candidatures.length === 0 ? (
        <Card className="bg-white border-slate-200 rounded-3xl p-8 text-center">
          <Send className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Aucune candidature pour l'instant</p>
          <p className="text-sm text-slate-400 mt-1">
            Consultez la Bourse à l'Emploi pour découvrir les offres disponibles
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {candidatures.map((candidature) => {
            const config = STATUT_CONFIG[candidature.statut] || STATUT_CONFIG['à_l_etude'];
            const StatusIcon = config.icon;
            
            return (
              <Card 
                key={candidature.id} 
                className={`bg-white border-l-4 rounded-2xl shadow-sm transition-all hover:shadow-md ${
                  candidature.statut === 'embauché' ? 'border-l-emerald-500' :
                  candidature.statut === 'entretien_prévu' ? 'border-l-amber-500' :
                  candidature.statut === 'non_retenu' ? 'border-l-red-500' :
                  'border-l-blue-500'
                }`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Statut */}
                      <Badge className={`${config.color} mb-3`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {config.label}
                      </Badge>
                      
                      {/* Titre du poste */}
                      <h3 className="font-bold text-lg text-slate-900 mb-1">
                        {candidature.offre_intitule || 'Offre'}
                      </h3>
                      
                      {/* Entreprise */}
                      {candidature.offre_entreprise && (
                        <p className="text-slate-600 flex items-center gap-1 mb-2">
                          <Building2 className="w-4 h-4" />
                          {candidature.offre_entreprise}
                        </p>
                      )}
                      
                      {/* Infos */}
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mb-3">
                        {candidature.offre_lieu && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {candidature.offre_lieu}
                          </span>
                        )}
                        {candidature.offre_type_contrat && (
                          <Badge variant="outline">{candidature.offre_type_contrat}</Badge>
                        )}
                        {candidature.offre_secteur && (
                          <Badge variant="outline">{candidature.offre_secteur}</Badge>
                        )}
                      </div>

                      {/* Date entretien si prévu */}
                      {candidature.statut === 'entretien_prévu' && candidature.date_entretien && (
                        <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 mb-3">
                          <p className="font-semibold text-amber-800 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Entretien prévu le {new Date(candidature.date_entretien).toLocaleDateString('fr-FR', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      )}

                      {/* Notes intervenant si présentes */}
                      {candidature.notes_intervenant && (
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                          <p className="text-sm text-slate-600">
                            <strong>Note de l'intervenant :</strong> {candidature.notes_intervenant}
                          </p>
                        </div>
                      )}
                      
                      {/* Description du statut */}
                      <p className="text-sm text-slate-500 mt-3 italic">{config.description}</p>
                    </div>
                    
                    {/* Date de candidature */}
                    <div className="text-right text-sm text-slate-400">
                      <p>Postulé le</p>
                      <p className="font-medium">
                        {new Date(candidature.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MesCandidatures;
